<template>
  <div class="flex gap-2">
    <CIcon
      class="font-900 color-primary3 mt-6 cursor-pointer flex-shrink-0"
      data-handle-group
      icon="grip-dots-vertical"
      size="20"
    />

    <div class="border-style-solid border-primary4 border-width-1 rounded-2 pa-4 flex-auto overflow-auto">
      <CCollapse v-model:expandedNames="expandedGroups" arrow-placement="left">
        <CCollapseItem :name="group.id">
          <template #header>
            <CTableInput
              :always-display-edit-icon="true"
              :class="{ 'w-full max-w-[400px]': getInputState(activeInputId === group.id) === 'edit' }"
              :max-length="64"
              :model-value="group.name"
              :state="getInputState(activeInputId === group.id)"
              value-class="color-text font-size-[18px] font-500"
              @cancel="clearActiveInput()"
              @edit="setActiveInput(group.id)"
              @update:modelValue="editGroupName(group.id, $event)"
            />
          </template>

          <template #header-extra>
            <div class="flex gap-2 items-center">
              <CButton
                v-tooltip="t('canvasUI.collectionSelector.chooseContent')"
                circle
                strong
                type="secondary"
                @click.stop="addSlides(group.id, filters)"
              >
                <template #icon>
                  <CIcon class="color-primary" icon="pen" size="16" />
                </template>
              </CButton>

              <CButton
                v-tooltip="t('canvasUI.collectionSelector.deleteGroup')"
                circle
                secondary
                strong
                type="error"
                @click.stop="deleteGroup(group.id, group.name)"
              >
                <template #icon>
                  <CIcon icon="trash" size="16" />
                </template>
              </CButton>
            </div>
          </template>

          <VueDraggable
            class="flex flex-col"
            :group="{
              name: 'slides',
            }"
            handle="[data-handle-slide]"
            :model-value="group.slides"
            v-bind="DragConfig"
            @update:model-value="reorderSlides(group.id, $event)"
          >
            <div v-for="(slide, index) in group.slides" :key="slide.id" class="flex">
              <div v-if="index !== 0" class="border-b-solid border-primary6"></div>

              <div class="flex gap-2 ma-2 flex-auto overflow-auto">
                <CIcon
                  class="color-primary4 flex-shrink-0 cursor-pointer font-900 self-center"
                  data-handle-slide
                  icon="grip-dots-vertical"
                  size="20"
                />

                <CollectionItem
                  :disabled-move-to-group="collection.groups.length === 1"
                  :is-first="index === 0"
                  :is-last="index === group.slides.length - 1"
                  :item="slide"
                  @moveDown="moveInDirection(group.id, slide, 1)"
                  @moveToGroup="openMoveSlideModal({ groupId: group.id, slide })"
                  @moveUp="moveInDirection(group.id, slide, -1)"
                />
              </div>
            </div>
          </VueDraggable>

          <div
            v-if="!group?.slides || group.slides.length === 0"
            class="bg-primary6 flex pa-4 justify-center items-center rounded-2"
          >
            <CButton
              class="border-style-solid border-primary5 border-width-1"
              iconPlacement="left"
              type="secondary"
              @click="addSlides(group.id, filters)"
            >
              {{ t('canvasUI.collectionSelector.chooseContent') }}
            </CButton>
          </div>
        </CCollapseItem>
      </CCollapse>
    </div>
  </div>
</template>

<script setup lang="ts">
import CCollapseItem from '@lib/components/CCollapse/CCollapseItem.vue'
import CTableInput from '@lib/components/CTableInput/CTableInput.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CCollapse from '@lib/components/CCollapse/CCollapse.vue'
import { VueDraggable } from 'vue-draggable-plus'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import { nextTick, ref } from 'vue'
import { useTableInput } from '@collection-selector/composables/table-input.use'
import { useAppStore } from '@collection-selector/stores/app.store'
import { useI18n } from 'vue-i18n'
import type { CollectionGroup, CollectionSlide } from '@collection-player/types/collection-player.type'
import CollectionItem from '@collection-selector/components/CollectionItem.vue'
import { useMoveSlideModal } from '@collection-selector/composables/move-slide-modal.use'
import { DragConfig } from '@collection-selector/constants'

defineProps<{
  group: CollectionGroup
  filters?: Record<string, any>
}>()

const { t } = useI18n()
const { open: openMoveSlideModal } = useMoveSlideModal()
const { getInputState, setActiveInput, clearActiveInput, activeInputId } = useTableInput()
const { collection, expandedGroups, deleteGroup, updateGroupName, updateSlides, updateGroups, addSlides } =
  useAppStore()

const slideOrderChangeQueue = ref<{ [groupId: string]: CollectionSlide[] }>({})

const moveInDirection = (groupId: string, slide: CollectionSlide, direction: -1 | 1) => {
  const group = collection.value.groups.find((g) => g.id === groupId)
  const slides = group?.slides

  if (!slides) return

  const orderedArray = moveItemInArray(
    [...slides],
    group?.slides.indexOf(slide),
    group?.slides.indexOf(slide) + direction,
  )

  updateSlides(groupId, orderedArray)
}

const editGroupName = (groupId: string, name: string) => {
  updateGroupName(groupId, name)
  clearActiveInput()
}

const reorderSlides = (groupId: string, slides: CollectionSlide[]) => {
  slideOrderChangeQueue.value[groupId] = slides

  updateGroups(
    collection.value.groups.map((group) =>
      group.id in slideOrderChangeQueue.value ? { ...group, slides: slideOrderChangeQueue.value[group.id] } : group,
    ),
  )

  nextTick(() => (slideOrderChangeQueue.value = {}))
}

function moveItemInArray(array: CollectionSlide[], oldIndex: number, newIndex: number) {
  const [item] = array.splice(oldIndex, 1)
  array.splice(Math.min(newIndex, array.length), 0, item)

  return array
}
</script>

<style lang="scss" scoped>
:deep(.n-collapse-item__header-main) {
  font-size: 15px;
  line-height: 18px !important;
  letter-spacing: 0.15px;
  padding: 8px !important;

  .n-collapse-item-arrow {
    margin-left: 0 !important;
    font-size: 12px !important;
    margin-right: 8px !important;
  }
}
</style>
